/**
 * Client-side DOCX → PDF for Word watermark prep.
 * Pipeline: docx-preview → html2canvas → letter pages with margins → pdf-lib.
 *
 * Pagination uses block boundaries (paragraphs/tables) so lines are not cut
 * in half at page edges. Margins are read from the DOCX section padding with
 * correct CSS unit conversion (pt/in/cm → px).
 */

import { renderAsync } from "docx-preview";
import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import { PDF_WATERMARK_MAX_PAGES } from "./renderPdfFirstPage";

/** US Letter @ 96dpi */
const PAGE_WIDTH_PX = 816; // 8.5in
const PAGE_HEIGHT_PX = 1056; // 11in
const CAPTURE_SCALE = 2;

/** Fallback margins matching this project's sample DOCX (1" / 1.25"). */
const DEFAULT_MARGINS_PX = {
  top: 96,
  bottom: 96,
  left: 120,
  right: 120,
};

function isDocxFile(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "");
  return (
    name.endsWith(".docx") ||
    type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
}

function isLegacyDoc(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "");
  return name.endsWith(".doc") || type === "application/msword";
}

function pdfFileNameFromWord(name) {
  const base =
    String(name || "document")
      .replace(/\.(docx|doc)$/i, "")
      .trim() || "document";
  return `${base}.pdf`;
}

/**
 * Convert CSS length (px/pt/in/cm/mm/pc) to CSS pixels @ 96dpi.
 * docx-preview sets margins as pt (e.g. "72pt") — parseFloat alone is wrong.
 */
function cssLengthToPx(value, fallback) {
  if (value == null || value === "") return fallback;
  const s = String(value).trim().toLowerCase();
  const m = s.match(/^(-?[\d.]+)\s*(px|pt|in|cm|mm|pc)?$/i);
  if (!m) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : fallback;
  }
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return fallback;
  switch (m[2] || "px") {
    case "px":
      return n;
    case "pt":
      return n * (96 / 72);
    case "in":
      return n * 96;
    case "cm":
      return n * (96 / 2.54);
    case "mm":
      return n * (96 / 25.4);
    case "pc":
      return n * 16;
    default:
      return fallback;
  }
}

function cleanupStaleWordConvertDom() {
  document
    .querySelectorAll(
      "[data-word-convert-host], [data-word-convert-shield], [data-word-capture-shell]"
    )
    .forEach((el) => {
      try {
        el.remove();
      } catch {
        // ignore
      }
    });
}

export { cleanupStaleWordConvertDom };

function createHost() {
  cleanupStaleWordConvertDom();

  // Keep the DOCX render in the layout tree for html2canvas, but fully
  // invisible and behind the app — never cover the prep UI with a shield.
  const host = document.createElement("div");
  host.setAttribute("data-word-convert-host", "1");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    `width:${PAGE_WIDTH_PX + 64}px`,
    `min-height:${PAGE_HEIGHT_PX}px`,
    "overflow:visible",
    "opacity:0",
    "pointer-events:none",
    "z-index:-1",
    "background:#ffffff",
  ].join(";");

  document.body.appendChild(host);
  return host;
}

function destroyHost(host) {
  try {
    host?.remove();
  } catch {
    // ignore
  }
  cleanupStaleWordConvertDom();
}

function injectLayoutOverrides(styleHost) {
  const style = document.createElement("style");
  style.setAttribute("data-word-convert-override", "1");
  style.textContent = `
    .docx-wrapper {
      background: #fff !important;
      padding: 0 !important;
      margin: 0 !important;
      display: block !important;
      width: ${PAGE_WIDTH_PX}px !important;
      max-width: ${PAGE_WIDTH_PX}px !important;
    }
    .docx-wrapper > section.docx {
      box-sizing: border-box !important;
      width: ${PAGE_WIDTH_PX}px !important;
      min-width: ${PAGE_WIDTH_PX}px !important;
      max-width: ${PAGE_WIDTH_PX}px !important;
      margin: 0 !important;
      box-shadow: none !important;
      background: #fff !important;
      overflow: visible !important;
      column-count: unset !important;
      display: block !important;
    }
    .docx-wrapper > section.docx > article {
      column-count: unset !important;
      width: 100% !important;
      max-width: 100% !important;
    }
  `;
  styleHost.appendChild(style);
}

function pageElements(host) {
  const wrapper = host.querySelector(".docx-wrapper");
  if (!wrapper) return [];
  const sections = Array.from(
    wrapper.querySelectorAll(":scope > section.docx, :scope > section")
  );
  if (sections.length) return sections;
  return Array.from(wrapper.children).filter((el) => el && el.nodeType === 1);
}

function waitForLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTimeout(resolve, 80));
    });
  });
}

function readSectionMargins(sectionEl) {
  const cs = window.getComputedStyle(sectionEl);
  return {
    top: Math.max(
      36,
      cssLengthToPx(cs.paddingTop, DEFAULT_MARGINS_PX.top)
    ),
    right: Math.max(
      36,
      cssLengthToPx(cs.paddingRight, DEFAULT_MARGINS_PX.right)
    ),
    bottom: Math.max(
      36,
      cssLengthToPx(cs.paddingBottom, DEFAULT_MARGINS_PX.bottom)
    ),
    left: Math.max(
      36,
      cssLengthToPx(cs.paddingLeft, DEFAULT_MARGINS_PX.left)
    ),
  };
}

function forcePageWidth(el) {
  el.style.setProperty("box-sizing", "border-box", "important");
  el.style.setProperty("width", `${PAGE_WIDTH_PX}px`, "important");
  el.style.setProperty("min-width", `${PAGE_WIDTH_PX}px`, "important");
  el.style.setProperty("max-width", `${PAGE_WIDTH_PX}px`, "important");
  el.style.setProperty("margin", "0", "important");
  el.style.setProperty("background", "#ffffff", "important");
  el.style.setProperty("column-count", "unset", "important");
  el.style.setProperty("display", "block", "important");
}

function canvasToPngBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Page encode failed"));
          return;
        }
        blob.arrayBuffer().then(
          (buf) => resolve(new Uint8Array(buf)),
          reject
        );
      },
      "image/png"
    );
  });
}

async function html2canvasEl(el, widthPx) {
  return html2canvas(el, {
    backgroundColor: "#ffffff",
    scale: CAPTURE_SCALE,
    useCORS: true,
    logging: false,
    width: widthPx,
    windowWidth: widthPx,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    onclone: (_doc, cloned) => {
      if (!cloned?.style) return;
      cloned.style.setProperty("width", `${widthPx}px`, "important");
      cloned.style.setProperty("max-width", `${widthPx}px`, "important");
      cloned.style.setProperty("box-sizing", "border-box", "important");
      cloned.style.setProperty("column-count", "unset", "important");
    },
  });
}

/**
 * Prefer breaks between block children so text is not sliced mid-line.
 * Falls back to fixed windows for oversized single blocks.
 */
function computeContentBreakYs(articleEl, contentHeightPx) {
  const total = Math.max(
    articleEl.scrollHeight || 0,
    articleEl.offsetHeight || 0,
    1
  );
  const starts = [0];
  let pageStart = 0;

  const kids = Array.from(articleEl.children).filter(
    (el) => el && el.nodeType === 1
  );

  if (!kids.length) {
    for (
      let y = contentHeightPx;
      y < total && starts.length < PDF_WATERMARK_MAX_PAGES;
      y += contentHeightPx
    ) {
      starts.push(y);
    }
    return { starts, total };
  }

  for (const kid of kids) {
    const top = kid.offsetTop;
    const bottom = top + Math.max(kid.offsetHeight, 1);

    if (bottom - pageStart > contentHeightPx && top > pageStart) {
      starts.push(top);
      pageStart = top;
    }

    // Oversized block (table/image): hard-slice inside it.
    while (
      bottom - pageStart > contentHeightPx &&
      starts.length < PDF_WATERMARK_MAX_PAGES
    ) {
      pageStart += contentHeightPx;
      starts.push(pageStart);
    }
  }

  return { starts, total };
}

function composePageFromStrip(sourceCanvas, srcY, srcH, margins) {
  const outW = PAGE_WIDTH_PX * CAPTURE_SCALE;
  const outH = PAGE_HEIGHT_PX * CAPTURE_SCALE;
  const mT = Math.round(margins.top * CAPTURE_SCALE);
  const mR = Math.round(margins.right * CAPTURE_SCALE);
  const mB = Math.round(margins.bottom * CAPTURE_SCALE);
  const mL = Math.round(margins.left * CAPTURE_SCALE);
  const contentW = Math.max(1, outW - mL - mR);
  const contentH = Math.max(1, outH - mT - mB);

  const page = document.createElement("canvas");
  page.width = outW;
  page.height = outH;
  const ctx = page.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outW, outH);

  const sliceH = Math.min(srcH, Math.max(0, sourceCanvas.height - srcY));
  if (sliceH <= 0) return page;

  // Scale source width → content box width; keep aspect for this slice height.
  const drawH = Math.min(
    contentH,
    Math.round((sliceH / Math.max(sourceCanvas.width, 1)) * contentW)
  );

  ctx.drawImage(
    sourceCanvas,
    0,
    srcY,
    sourceCanvas.width,
    sliceH,
    mL,
    mT,
    contentW,
    drawH
  );

  return page;
}

/**
 * Paginate a captured content strip using CSS-space break Ys.
 */
function paginateWithBreaks(contentCanvas, breaks, margins) {
  const { starts, total } = breaks;
  const scaleY = contentCanvas.height / Math.max(total, 1);
  const pages = [];

  for (let i = 0; i < starts.length; i += 1) {
    if (pages.length >= PDF_WATERMARK_MAX_PAGES) break;
    const y0 = starts[i];
    const y1 = i + 1 < starts.length ? starts[i + 1] : total;
    const srcY = Math.round(y0 * scaleY);
    const srcH = Math.max(1, Math.round((y1 - y0) * scaleY));
    pages.push(composePageFromStrip(contentCanvas, srcY, srcH, margins));
  }

  return pages.length
    ? pages
    : [composePageFromStrip(contentCanvas, 0, contentCanvas.height, margins)];
}

async function sectionToLetterPages(sectionEl) {
  const margins = readSectionMargins(sectionEl);
  forcePageWidth(sectionEl);

  const article =
    sectionEl.querySelector(":scope > article") ||
    sectionEl.querySelector("article") ||
    sectionEl;

  const contentWidth = Math.max(
    200,
    Math.round(PAGE_WIDTH_PX - margins.left - margins.right)
  );
  const contentHeight = Math.max(
    200,
    Math.round(PAGE_HEIGHT_PX - margins.top - margins.bottom)
  );

  sectionEl.style.setProperty("padding", "0px", "important");
  // Make article a predictable block formatting context for offsetTop.
  article.style.setProperty("position", "relative", "important");
  article.style.setProperty("width", `${contentWidth}px`, "important");
  article.style.setProperty("max-width", `${contentWidth}px`, "important");
  await waitForLayout();

  let contentCanvas;
  let breaks;
  try {
    breaks = computeContentBreakYs(article, contentHeight);
    contentCanvas = await html2canvasEl(article, contentWidth);
  } finally {
    sectionEl.style.setProperty("padding-top", `${margins.top}px`, "important");
    sectionEl.style.setProperty(
      "padding-right",
      `${margins.right}px`,
      "important"
    );
    sectionEl.style.setProperty(
      "padding-bottom",
      `${margins.bottom}px`,
      "important"
    );
    sectionEl.style.setProperty(
      "padding-left",
      `${margins.left}px`,
      "important"
    );
  }

  // Collapsed capture → capture full section and fall back to fixed windows.
  if (contentCanvas.width < contentWidth * CAPTURE_SCALE * 0.5) {
    forcePageWidth(sectionEl);
    await waitForLayout();
    const full = await html2canvasEl(sectionEl, PAGE_WIDTH_PX);
    const mT = Math.round(margins.top * CAPTURE_SCALE);
    const mB = Math.round(margins.bottom * CAPTURE_SCALE);
    const mL = Math.round(margins.left * CAPTURE_SCALE);
    const mR = Math.round(margins.right * CAPTURE_SCALE);
    const bandH = Math.max(1, full.height - mT - mB);
    const crop = document.createElement("canvas");
    crop.width = Math.max(1, full.width - mL - mR);
    crop.height = bandH;
    const cctx = crop.getContext("2d");
    cctx.fillStyle = "#ffffff";
    cctx.fillRect(0, 0, crop.width, crop.height);
    cctx.drawImage(
      full,
      mL,
      mT,
      crop.width,
      bandH,
      0,
      0,
      crop.width,
      bandH
    );
    const cssTotal = bandH / CAPTURE_SCALE;
    const fixed = { starts: [0], total: cssTotal };
    for (
      let y = contentHeight;
      y < cssTotal && fixed.starts.length < PDF_WATERMARK_MAX_PAGES;
      y += contentHeight
    ) {
      fixed.starts.push(y);
    }
    return paginateWithBreaks(crop, fixed, margins);
  }

  return paginateWithBreaks(contentCanvas, breaks, margins);
}

/**
 * Convert a Word (.docx) File into a PDF File.
 */
export async function convertWordToPdfFile(file, opts = {}) {
  if (!file) throw new Error("No Word file");
  if (isLegacyDoc(file) && !isDocxFile(file)) {
    throw new Error(
      "Legacy .doc isn’t supported here — please save as .docx and try again."
    );
  }
  if (!isDocxFile(file)) {
    throw new Error("Please choose a Word document (.docx).");
  }

  const maxPages = opts.maxPages ?? PDF_WATERMARK_MAX_PAGES;
  const onProgress =
    typeof opts.onProgress === "function" ? opts.onProgress : null;

  onProgress?.({ stage: "render" });

  const host = createHost();
  const styleHost = document.createElement("div");
  host.appendChild(styleHost);
  const bodyHost = document.createElement("div");
  bodyHost.style.width = `${PAGE_WIDTH_PX}px`;
  bodyHost.style.maxWidth = `${PAGE_WIDTH_PX}px`;
  host.appendChild(bodyHost);

  try {
    const buffer = await file.arrayBuffer();
    await renderAsync(buffer, bodyHost, styleHost, {
      className: "docx",
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      breakPages: true,
      renderHeaders: true,
      renderFooters: true,
      useBase64URL: true,
    });

    injectLayoutOverrides(styleHost);
    await waitForLayout();

    const sections = pageElements(bodyHost);
    if (!sections.length) {
      throw new Error("Could not render pages from this Word document.");
    }

    sections.forEach(forcePageWidth);
    await waitForLayout();

    const totalScrollH = Math.max(
      ...sections.map((el) => el.scrollHeight || el.offsetHeight || 0),
      PAGE_HEIGHT_PX
    );
    host.style.height = `${Math.min(totalScrollH + 120, 20000)}px`;
    host.style.overflow = "visible";

    const out = await PDFDocument.create();
    let pageIndex = 0;

    for (let s = 0; s < sections.length; s += 1) {
      if (pageIndex >= maxPages) break;

      const el = sections[s];
      onProgress?.({
        stage: "capture",
        page: pageIndex + 1,
        total: Math.min(maxPages, sections.length),
      });

      const letterPages = await sectionToLetterPages(el);

      for (const canvas of letterPages) {
        if (pageIndex >= maxPages) break;
        onProgress?.({
          stage: "capture",
          page: pageIndex + 1,
          total: Math.min(maxPages, pageIndex + letterPages.length),
        });

        const pngBytes = await canvasToPngBytes(canvas);
        const embedded = await out.embedPng(pngBytes);
        const page = out.addPage([612, 792]);
        page.drawImage(embedded, {
          x: 0,
          y: 0,
          width: 612,
          height: 792,
        });
        pageIndex += 1;
      }
    }

    if (pageIndex === 0) {
      throw new Error("Could not build PDF pages from this Word document.");
    }

    onProgress?.({ stage: "assemble", page: pageIndex, total: pageIndex });
    const pdfBytes = await out.save();
    return new File([pdfBytes], pdfFileNameFromWord(file.name), {
      type: "application/pdf",
      lastModified: Date.now(),
    });
  } finally {
    destroyHost(host);
  }
}
